import { useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';

interface VideoPlayerProps {
    videoUrl: string;
    onProgress: (played: number, maxPlayed: number) => void;
    maxPlayed: number;
    initialPlayedSeconds?: number;
    onDuration?: (duration: number) => void;
}

// Encode only the path of the URL, preserving the query string and fragment.
// The previous implementation encoded each `/`-separated segment, which
// corrupted signed URLs (the `?token=...` got encoded into the path).
function safeEncodeVideoUrl(rawUrl: string): string {
    try {
        const url = new URL(rawUrl);
        url.pathname = url.pathname
            .split('/')
            .map(segment => (segment ? encodeURIComponent(decodeURIComponent(segment)) : segment))
            .join('/');
        return url.toString();
    } catch {
        return rawUrl;
    }
}

export function VideoPlayer({ videoUrl, onProgress, maxPlayed, initialPlayedSeconds, onDuration }: VideoPlayerProps) {
    const playerRef = useRef<ReactPlayer>(null);
    const hasSeekedRef = useRef(false);

    const encodedVideoUrl = safeEncodeVideoUrl(videoUrl);

    const handleProgress = (state: { played: number }) => {
        // Allow free seeking - just track progress without restrictions
        onProgress(state.played, Math.max(maxPlayed, state.played));
    };

    const handleError = (error: any) => {
        console.error('Video playback error:', error);
        console.error('Original Video URL:', videoUrl);
        console.error('Encoded Video URL:', encodedVideoUrl);
        console.error('File extension:', videoUrl.split('.').pop());
    };

    const handleReady = () => {
        // Seek to the persisted position once per video load.
        if (!hasSeekedRef.current && initialPlayedSeconds && initialPlayedSeconds > 1) {
            hasSeekedRef.current = true;
            playerRef.current?.seekTo(initialPlayedSeconds, 'seconds');
        }
    };

    // Reset the seek guard when the video changes so the next video can restore from its own checkpoint.
    useEffect(() => {
        hasSeekedRef.current = false;
    }, [videoUrl]);

    // Test if the file is accessible
    useEffect(() => {
        const testUrl = async () => {
            try {
                const response = await fetch(encodedVideoUrl, { method: 'HEAD' });
                console.log('📊 File accessibility test:', {
                    status: response.status,
                    statusText: response.statusText,
                    contentType: response.headers.get('content-type'),
                    contentLength: response.headers.get('content-length'),
                    url: encodedVideoUrl
                });
            } catch (error) {
                console.error('❌ File accessibility test failed:', error);
            }
        };

        if (videoUrl) {
            testUrl();
        }
    }, [videoUrl, encodedVideoUrl]);

    return (
        <div className="relative w-full h-full">
            <ReactPlayer
                ref={playerRef}
                url={encodedVideoUrl}
                width="100%"
                height="100%"
                controls
                onProgress={handleProgress}
                onError={handleError}
                onReady={handleReady}
                onDuration={onDuration}
                progressInterval={1000}
                config={{
                    file: {
                        attributes: {
                            controlsList: 'nodownload',
                            crossOrigin: 'anonymous',
                            playsInline: true,
                            preload: 'auto',
                        },
                        forceVideo: true,
                    },
                    youtube: {
                        playerVars: {
                            modestbranding: 1,
                            rel: 0,
                            showinfo: 0,
                        }
                    }
                }}
            />
        </div>
    );
}
